// Geocoding utilities for web app
// Similar to mobile app's geocoding.ts but adapted for web

import { supabase } from '../supabase';

export interface AddressData {
  street?: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  formatted_address?: string;
  place_id?: string;
  business_name?: string;
}

export interface GeocodingResult {
  latitude: number;
  longitude: number;
  formatted_address: string;
  address_components: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
}

// Google Maps API key is now server-side only (in edge functions)
// Removed: const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Extract address component by type
 */
export function getAddressComponent(
  components: GeocodingResult['address_components'],
  type: string,
  useShortName = false
): string {
  if (!Array.isArray(components)) return '';
  
  const component = components.find((comp) => 
    Array.isArray(comp.types) && comp.types.includes(type)
  );
  
  return component ? (useShortName ? component.short_name : component.long_name) : '';
}

/**
 * Parse Google Places result into standardized address format
 * 
 * Stores place_id for future use cases:
 * - Google Reviews API: Fetch and analyze customer reviews
 * - Business Insights: Track rating trends, review volume, sentiment
 * - Revenue Recommendations: Data-driven suggestions based on review patterns
 * - Competitive Analysis: Compare performance against similar businesses
 */
export function parseGooglePlaceResult(result: {
  address_components?: Array<{
    types: string[];
    long_name: string;
    short_name: string;
  }>;
  formatted_address?: string;
  place_id?: string;
  geometry?: {
    location?: {
      lat?: number | (() => number);
      lng?: number | (() => number);
    };
  };
  name?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  types?: string[];
}): Partial<AddressData> {
  if (!result || !result.address_components) {
    return {};
  }

  const components = result.address_components;
  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route');
  const subpremise = getAddressComponent(components, 'subpremise');
  
  // Build street address - handle both numbered addresses and business names
  let street = '';
  if (streetNumber && route) {
    // Standard address: "123 Main St"
    street = `${streetNumber} ${route}`.trim();
  } else if (route) {
    // Address without number: "Main St"
    street = route;
  } else if (result.formatted_address) {
    // Fallback: use first line of formatted address
    const firstLine = result.formatted_address.split(',')[0];
    street = firstLine.trim();
  }
  
  // Extract business name separately for establishments
  const businessName = result.name && result.types?.includes('establishment') ? result.name : undefined;
  
  // Handle both Google Maps API objects (methods) and plain JSON (properties)
  let latitude: number | undefined;
  let longitude: number | undefined;
  
  if (result.geometry?.location) {
    const location = result.geometry.location;
    // Check if lat/lng are functions (Google Maps API objects) or numbers (plain JSON)
    if (typeof location.lat === 'function') {
      latitude = location.lat();
    } else if (typeof location.lat === 'number') {
      latitude = location.lat;
    }
    
    if (typeof location.lng === 'function') {
      longitude = location.lng();
    } else if (typeof location.lng === 'number') {
      longitude = location.lng;
    }
  }
  
  return {
    street,
    unit: subpremise || '',
    city: getAddressComponent(components, 'locality') || 
          getAddressComponent(components, 'sublocality') ||
          getAddressComponent(components, 'administrative_area_level_2'),
    state: getAddressComponent(components, 'administrative_area_level_1', true),
    zip: getAddressComponent(components, 'postal_code'),
    country: getAddressComponent(components, 'country', true),
    formatted_address: result.formatted_address,
    latitude,
    longitude,
    place_id: result.place_id, // Store Google Place ID for future queries
    business_name: businessName, // Store business name for establishments
  };
}

/**
 * Get coordinates from a place ID
 */
export async function getCoordinatesFromPlaceId(placeId: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const { data, error } = await supabase.functions.invoke('google-geocode', {
      body: { place_id: placeId },
    });

    if (error) {
      console.error('[geocoding] Error calling edge function:', error);
      return null;
    }

    if (data?.status === 'OK' && data.results && data.results.length > 0) {
      const location = data.results[0].geometry.location;
      return {
        lat: location.lat,
        lng: location.lng,
      };
    }

    return null;
  } catch (error) {
    console.error('[geocoding] Error fetching coordinates from place ID:', error);
    return null;
  }
}

/**
 * Returns the IANA timezone string for given lat/lng using Google Time Zone API.
 * Falls back to browser timezone if the API call fails.
 */
export async function getTimezoneFromLatLng(lat: number, lng: number): Promise<string> {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (apiKey) {
    try {
      const timestamp = Math.floor(Date.now() / 1000);
      const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'OK' && data.timeZoneId) return data.timeZoneId;
      }
    } catch (err) {
      console.warn('[geocoding] Timezone API call failed, using browser timezone:', err);
    }
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Common US timezone options for manual fallback selection.
 */
export const US_TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
];

/**
 * Validate address completeness
 */
export function validateAddress(address: Partial<AddressData>, requiredFields: (keyof AddressData)[]): {
  isValid: boolean;
  missingFields: string[];
} {
  const missingFields: string[] = [];
  
  for (const field of requiredFields) {
    if (!address[field] || (typeof address[field] === 'string' && address[field].trim() === '')) {
      missingFields.push(field);
    }
  }
  
  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
}
